import { useEffect, useState } from "react";
import { TrendingUp, BarChart3, Car, CarFront, Truck, Wallet, Home, Wrench, AlertTriangle, ShieldAlert, ClipboardList, FileText, Table2 } from "lucide-react";
import Skeleton from '@mui/material/Skeleton';
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { Table, TableBody, TableBuildingTag, TableCard, TableCell, TableHead, TableHeaderWrap, TableRow } from "../../../../component/table/table-layout";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card, CardContent, CardHeader, CardTitle, KPI, TitleHeading } from "../../../../component/ui/layout";

const FleetCarOverview = () => {
  const apiCall = useApiCall();
  const [KPIData, setKPIData] = useState({});
  const [KPILoading, setKPILoading] = useState(false);

  const getKPIData = async () => {
    setKPILoading(false);
    try {
      const resp = await apiCall({ method: "get", url: `fleet-overview/dashboard` });
      setKPIData(resp);
      // const trend = resp?.monthly_fleet_cost_trend;
      // setTrendData({
      //   month: trend?.map(i => i?.month),
      //   car_rent: trend?.map(i => i?.car_rent),
      //   van_rent: trend?.map(i => i?.van_rent),
      //   cash_allowance: trend?.map(i => i?.cash_allowance),
      //   fuel_allowance: trend?.map(i => i?.fuel_allowance),
      //   maintenance_cost: trend?.map(i => i?.maintenance_cost),
      // });
      // setEmployeeData(resp?.fleet_composition_employee_chart);
      // setVehicleData(resp?.fleet_composition_vehicle_chart);
    } catch (error) {
      console.warn("Error : ", error);
    } finally {
      setKPILoading(true);
    }
  }

  useEffect(() => {
    getKPIData();
  }, []);

  // LINE CHART
  const [trendData, setTrendData] = useState({});
  const lineOptions = {
    chart: { type: "line", backgroundColor: "transparent", height: 300 },
    title: { text: "" },
    xAxis: {
      categories: trendData?.month,
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },
    yAxis: {
      title: { text: "Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },
    legend: { align: "right", verticalAlign: "top" },
    series: [
      { name: "Car Rent", data: trendData?.car_rent || [], color: "#2563EB" },
      { name: "Van Rent", data: trendData?.van_rent || [], color: "#10B981" },
      { name: "Cash Allowance", data: trendData?.cash_allowance || [], color: "#F59E0B" },
      { name: "Fuel Allowance", data: trendData?.fuel_allowance || [], color: "#EF4444" },
      { name: "Maintenance Cost", data: trendData?.maintenance_cost || [], color: "#8B5CF6" },
    ],
    credits: { enabled: false },
  };

  // PIE CHART
  const [employeeData, setEmployeeData] = useState({});
  const [vehicleData, setVehicleData] = useState({});
  const [activePie, setActivePie] = useState("employee");
  const getPieSeries = () => {
    const source =
      activePie === "employee"
        ? employeeData
        : vehicleData;

    return Object.entries(source || {}).map(([key, val]) => ({
      count: val,
      name: key.replaceAll("_", " ").replaceAll("employees", ""),
      y: parseFloat(val),
    }));
  };
  const pieOptions = {
    chart: { type: "pie" },
    title: "",
    credits: { enabled: false },
    tooltip: {
      pointFormat:
        "{series.name}: <b>{point.count}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>",
    },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}% ({point.count})",
        },
      },
    },
    series: [
      {
        name: activePie === "employee" ? "Employees" : "Vehicles",
        colorByPoint: true,
        data: getPieSeries(),
      },
    ],
    legend: { enabled: false },
  };

  return (
    <>
      <div className="pt-[20px] overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div>
            <TitleHeading>Cars Overview</TitleHeading>
          </div>

          <TableCard className={"!p-[15px] !pr-[7px] h-full overflow-hidden"}>
            <div className="pb-2">
              <TitleHeading>Fleet Overview</TitleHeading>
            </div>
            {/* KPI Row */}
            <div className="grid grid-cols-5 gap-4">
              {KPILoading ? (
                <>
                  {/* <KPI
                    icon={Car}
                    label="Total Vehicles"
                    value={KPIData?.fleet_overview?.total_vehicles}
                    sublabel={"Complete Fleet Count"}
                    /> */}
                  <KPI
                    icon={CarFront}
                    label="Active Cars"
                    value={KPIData?.fleet_overview?.active_cars}
                    sublabel={`Cars Currently Active`}
                    extraClass={'theme'}
                  />
                  <KPI
                    icon={Wrench}
                    label="Under Repair"
                    value={KPIData?.fleet_overview?.under_repair}
                    sublabel={`Vehicles in Workshop`}
                    extraClass={'yellow'}
                  />
                  <KPI
                    icon={AlertTriangle}
                    label="Overdue Maintenance"
                    value={KPIData?.fleet_overview?.overdue_maintenance}
                    sublabel={`Maintenance Pending`}
                    extraClass={'red'}
                  />
                  <KPI
                    icon={ShieldAlert}
                    label="Uninsured Vehicles"
                    value={KPIData?.fleet_overview?.uninsured}
                    sublabel={`Insurance Missing`}

                  />
                  <KPI
                    icon={Wallet}
                    label="Cash Allowance Users"
                    value={KPIData?.fleet_overview?.cash_allowance_employees}
                    sublabel={`Employees Receiving Allowance`}

                  />
                </>
              ) : (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="bg-white shadow rounded-xl p-4 animate-pulse">
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="pb-2 pt-4">
              <TitleHeading>Monthly Cost Summary</TitleHeading>
            </div>
            {/* KPI Row */}
            <div className="grid grid-cols-5 gap-4">
              {KPILoading ? (
                <>
                  <KPI
                    icon={Car}
                    label="Car Rent"
                    value={KPIData?.monthly_cost_summary?.total_car_rent}
                    sublabel={"Total Rent Cost"}
                    extraClass={'theme'}
                  />
                  <KPI
                    icon={Wallet}
                    label="Cash Allowance"
                    value={KPIData?.monthly_cost_summary?.cash_allowance_payout}
                    sublabel={`Monthly Payout`}

                  />
                  <KPI
                    icon={Wrench}
                    label="Maintenance Cost"
                    value={KPIData?.monthly_cost_summary?.maintenance_mtd}
                    sublabel={`Month to Date`}
                  />
                  <KPI
                    icon={ClipboardList}
                    label="Repair Cost"
                    value={KPIData?.monthly_cost_summary?.repairs_mtd}
                    sublabel={`Current Month`}
                    extraClass={'green'}
                  />
                  {/* <KPI
                    icon={Car}
                    label="Total Fleet Cost"
                    value={KPIData?.monthly_cost_summary?.total_fleet_cost}
                    sublabel={`All Expenses`}

                  /> */}
                  <KPI
                    icon={Truck}
                    label="Avg Cost / Vehicle"
                    value={KPIData?.monthly_cost_summary?.avg_cost_per_vehicle}
                    sublabel={`Monthly Average`}

                  />
                </>
              ) : (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="bg-white shadow rounded-xl p-4 animate-pulse">
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="pb-2 pt-4">
              <TitleHeading>Compliance Alerts</TitleHeading>
            </div>
            {/* KPI Row */}
            <div className="grid grid-cols-5 gap-4">
              {KPILoading ? (
                <>
                  <KPI
                    icon={FileText}
                    label="Licenses Expiring"
                    value={KPIData?.compliance_alerts?.licenses_expiring_30}
                    sublabel={"Next 30 Days"}
                    extraClass={'theme'}
                  />
                  <KPI
                    icon={AlertTriangle}
                    label="Expired Licenses"
                    value={KPIData?.compliance_alerts?.licenses_expired}
                    sublabel={`Immediate Action`}

                  />
                  <KPI
                    icon={ClipboardList}
                    label="Contracts Expiring"
                    value={KPIData?.compliance_alerts?.contracts_expiring_30}
                    sublabel={`Within 30 Days`}
                  />
                  <KPI
                    icon={ShieldAlert}
                    label="Insurance Claims"
                    value={KPIData?.compliance_alerts?.pending_insurance_claims}
                    sublabel={`Pending Approval`}

                  />
                  <KPI
                    icon={AlertTriangle}
                    label="Damage Reports"
                    value={KPIData?.compliance_alerts?.open_damage_reports}
                    sublabel={`Open Cases`}

                  />
                  {/* <KPI
                    icon={Wrench}
                    label="Maintenance (Current Month)"
                    value={KPIData?.compliance_alerts?.avg_cost_per_vehicle}
                    sublabel={`Repairs This Month`}

                  /> */}
                </>
              ) : (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="bg-white shadow rounded-xl p-4 animate-pulse">
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-10 gap-6 mt-6">
              <Card className="col-span-5 min-h-[450px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Table2 className="h-5 w-5" />
                    Vendor Cost Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell header><TableHeaderWrap><span>Vendor</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Vehicles</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Monthly Cost</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Percent of Total</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>AVG. Per Vehicle</span></TableHeaderWrap></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {KPIData?.vendor_cost_breakdown?.map((ele, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{ele?.vendor}</TableCell>
                          <TableCell>{ele?.vehicles}</TableCell>
                          <TableCell>{ele?.monthly_cost}</TableCell>
                          <TableCell>{ele?.percent_of_total}</TableCell>
                          <TableCell>{ele?.avg_per_vehicle}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="col-span-5 min-h-[450px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Table2 className="h-5 w-5" />
                    Month Over Month Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell header><TableHeaderWrap><span>Metric</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Current</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Previous</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Change</span></TableHeaderWrap></TableCell>
                        <TableCell header><TableHeaderWrap><span>Status</span></TableHeaderWrap></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {KPIData?.month_over_month_trend?.map((ele, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{ele?.metric}</TableCell>
                          <TableCell>{ele?.current}</TableCell>
                          <TableCell>{ele?.previous}</TableCell>
                          <TableCell>{ele?.change}</TableCell>
                          <TableCell>{ele?.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>


              {/* <Card className="col-span-6 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Monthly Fleet Cost Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {
                    KPILoading ? (
                      <HighchartsReact highcharts={Highcharts} options={lineOptions} />
                    ) : (
                      <InnerLoader />
                    )
                  }
                </CardContent>
              </Card>

              <Card className="col-span-4 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 capitalize">
                    <BarChart3 className="h-5 w-5" /> Fleet Composition By {activePie}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`flex items-center justify-end gap-[15px]`}>
                    {["employee", "vehicle"].map(label => (
                      <div
                        key={label}
                        className={`flex items-center gap-[3px] cursor-pointer`}
                        onClick={() => setActivePie(label)}
                      >
                        <div
                          className={`relative h-[1.5px] w-[16px] cursor-pointer ${activePie === label ? "bg-[#1FC16B]" : "bg-[#787878]"}`}
                        >
                          <div className={`absolute top-1/2 left-[4px] h-[8px] w-[8px] rounded-full -translate-y-1/2 ${activePie === label ? "bg-[#1FC16B]" : "bg-[#787878]"}`} />
                        </div>
                        <p className="text-sm font-medium text-[#333] capitalize">{label}</p>
                      </div>
                    ))}
                  </div>

                  {
                    KPILoading ? (
                      <HighchartsReact highcharts={Highcharts} options={pieOptions} />
                    ) : (
                      <InnerLoader />
                    )
                  }
                </CardContent>
              </Card> */}
            </div>
          </TableCard>
        </div>
      </div>
    </>
  );
}

export default FleetCarOverview